import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot, Search, ChevronDown, ArrowRight, Sparkles } from 'lucide-react';
import { Agent } from '../types';
import { AppCard } from '../components/base/AppCard';
import { AppBadge } from '../components/base/AppBadge';
import * as mockApi from '../services/mockApi';
export function AppAIPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [agents, setAgents] = useState<Agent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    loadAgents();
  }, []);
  const loadAgents = async () => {
    setIsLoading(true);
    try {
      const data = await mockApi.getAgents();
      setAgents(data);
    } catch (error) {
      console.error('Failed to load agents', error);
    } finally {
      setIsLoading(false);
    }
  };
  const filteredAgents = agents.filter(agent => {
    const matchesSearch = searchQuery === '' || agent.name.toLowerCase().includes(searchQuery.toLowerCase()) || agent.type.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = typeFilter === 'all' || agent.type === typeFilter;
    return matchesSearch && matchesType;
  });
  const uniqueTypes = Array.from(new Set(agents.map(a => a.type)));
  const handleAgentClick = (agentId: string) => {
    navigate(`/servizi/${agentId}`);
  };
  if (isLoading) {
    return <div className="flex items-center justify-center py-20" data-id="element-996">
        <p className="text-gray-500 dark:text-gray-400" data-id="element-997">Caricamento...</p>
      </div>;
  }
  return <div className="pb-12 space-y-8" data-id="element-998">
      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4" data-id="element-999">
        <div className="flex-1 relative" data-id="element-1000">
          <Search className="w-4 h-4 text-gray-400 dark:text-gray-500 absolute left-3.5 top-3" data-id="element-1001" />
          <input type="text" placeholder="Cerca servizi..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-subtle rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-180 shadow-sm" data-id="element-1002" />
        </div>

        <div className="relative min-w-[200px]" data-id="element-1003">
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="appearance-none w-full bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-subtle text-gray-700 dark:text-gray-300 py-2.5 pl-3.5 pr-10 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 cursor-pointer transition-all duration-180 shadow-sm" data-id="element-1004">
            <option value="all" data-id="element-1005">Tutti i tipi</option>
            {uniqueTypes.map(type => <option key={type} value={type} data-id="element-1006">
                {type}
              </option>)}
          </select>
          <ChevronDown className="w-4 h-4 text-gray-400 dark:text-gray-500 absolute right-3 top-3 pointer-events-none" data-id="element-1007" />
        </div>
      </div>

      {/* Agent Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1008">
        {filteredAgents.map(agent => <AppCard key={agent.id} className="group cursor-pointer transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 flex flex-col h-full" onClick={() => handleAgentClick(agent.id)} data-id="element-1009">
            <div className="flex items-start justify-between mb-4" data-id="element-1010">
              <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-blue-600 dark:text-blue-400 group-hover:scale-110 transition-transform duration-200" data-id="element-1011">
                <Sparkles className="w-6 h-6" data-id="element-1012" />
              </div>
              <AppBadge variant="blue" data-id="element-1013">{agent.type}</AppBadge>
            </div>

            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" data-id="element-1014">
              {agent.name}
            </h3>

            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-6 line-clamp-2 flex-1" data-id="element-1015">
              {agent.shortDescription || 'Servizio configurato per la piattaforma.'}
            </p>

            <div className="pt-4 border-t border-gray-100 dark:border-dark-subtle flex items-center justify-between text-sm font-medium" data-id="element-1016">
              <span className="text-gray-500 dark:text-gray-400" data-id="element-1017">
                Configura
              </span>
              <div className="flex items-center text-blue-600 dark:text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200 transform translate-x-[-10px] group-hover:translate-x-0" data-id="element-1018">
                <span className="mr-1" data-id="element-1019">Apri</span>
                <ArrowRight className="w-4 h-4" data-id="element-1020" />
              </div>
            </div>
          </AppCard>)}
      </div>

      {filteredAgents.length === 0 && <div className="flex flex-col items-center justify-center py-20 text-center" data-id="element-1021">
          <div className="w-16 h-16 bg-gray-50 dark:bg-dark-subtle rounded-full flex items-center justify-center mb-4" data-id="element-1022">
            <Bot className="w-8 h-8 text-gray-400 dark:text-gray-500" data-id="element-1023" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1" data-id="element-1024">
            Nessun servizio trovato
          </h3>
          <p className="text-gray-500 dark:text-gray-400" data-id="element-1025">
            Prova a modificare i filtri di ricerca
          </p>
        </div>}
    </div>;
}